<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :offset-x="offsetX"
    :offset-y="offsetY"
    min-width="auto">
    <template v-slot:activator="{ on, attrs }">
      <div class="d-flex align-center">
        <v-text-field
          v-bind="attrs"
          v-model="internalValue"
          :filled="filled"
          :label="label"
          :prepend-inner-icon="prependInnerIcon"
          :error="error"
          :error-messages="errorMessages"
          :hide-details="hideDetails"
          @blur="onTextFieldBlur"/>
        <vf-tooltip :text="$t('open_time_picker')">
          <v-btn fab small text v-bind="attrs" v-on="on" class="mb-1" tabindex="-1">
            <v-icon color="primary">mdi-timer-edit</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </template>
    <v-time-picker
      v-model="internalValue"
      :format="format"
      :no-title="noTitle"/>
  </v-menu>
</template>

<script>
import VfTooltip from '@/components/shared/VfTooltip'

/**
 * Wrapper for v-timepicker for use in Verofresh app.
 */
export default {
  name: 'TimePicker',
  components: {
    VfTooltip
  },
  props: {
    value: String,
    label: String,
    /**
     * Pass-through property to 'prependInnerIcon' on the read-only text field.
     */
    prependInnerIcon: String,
    /**
     * Pass-through property to 'filled' on the read-only text field.
     */
    filled: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through property for the 'error' property (delegates to the text box)
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through property for the 'error-messages' property (delegates to the text box)
     */
    errorMessages: {
      default: () => []
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    offsetX: {
      type: Boolean,
      default: false
    },
    offsetY: {
      type: Boolean,
      default: true
    },
    format: {
      type: String,
      default: null
    },
    noTitle: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // The display flag for the v-menu component.
      menu: false
    }
  },
  computed: {
    internalValue: {
      get: function () {
        return this.value
      },
      set: function (newValue) {
        this.$emit('input', newValue)
      }
    }
  },
  methods: {
    /**
     * Clear the component's values.
     */
    clear () {
      this.internalValue = null
      this.menu = false
    },
    /**
     * When the text field is blurred, try to autocomplete any text into a time.
     */
    onTextFieldBlur () {
      if (this.internalValue) {
        const value = this.internalValue.trim()
        let newValue = null
        // a -> 0a:00
        if (/^\d$/.test(value)) {
          newValue = `0${value}:00`
        // ab -> ab:00
        } else if (/^\d\d$/.test(value)) {
          newValue = `${value[0]}${value[1]}:00`
        // a:b -> 0a:b0
        } else if (/^\d:\d$/.test(value)) {
          newValue = `0${value[0]}:${value[2]}0`
        // abc -> 0a:bc
        } else if (/^\d\d\d$/.test(value)) {
          newValue = `0${value[0]}:${value[1]}${value[2]}`
        // a:bc -> 0a:bc
        } else if (/^\d:\d\d$/.test(value)) {
          newValue = `0${value[0]}:${value[2]}${value[3]}`
        // ab:c -> ab:c0
        } else if (/^\d\d:\d$/.test(value)) {
          newValue = `${value[0]}${value[1]}:${value[3]}0`
        // abcd -> ab:cd
        } else if (/^\d\d\d\d$/.test(value)) {
          newValue = `${value[0]}${value[1]}:${value[2]}${value[3]}`
        // ab:cd -> ab:cd (already valid)
        } else if (/^\d\d:\d\d$/.test(value)) {
          newValue = value
        }
        if (newValue !== null) {
          const split = newValue.split(':')
          // Check that hour is not greater than 23 or minute is greater than 59. If so, reset newValue to null.
          if (parseInt(split[0]) > 23 || parseInt(split[1]) > 59) {
            newValue = null
          }
        }
        this.internalValue = newValue
      }
    }
  }
}
</script>

<style scoped>

</style>
